import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UserCheck, Compass, ShieldCheck, Loader2, Plus, FileSignature } from "lucide-react";

export const MiningSurveyorPortal = () => {
  const qc = useQueryClient();
  const [pkg, setPkg] = useState({
    surveyor_name: "", surveyor_reg: "", package_name: "",
    province: "Matabeleland South", commodity: "Gold", declared_crs: "EPSG:20936",
  });

  const ref = useQuery({ queryKey: ["mm-ref"], queryFn: async () => (await api.get("/cadastre360/migration/reference")).data });
  const list = useQuery({ queryKey: ["mm-surveyor-list"], queryFn: async () => (await api.get("/cadastre360/migration/surveyor/submissions")).data });

  const submit = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/migration/surveyor/submissions", pkg)).data,
    onSuccess: (d) => {
      toast.success(`Package submitted · signature ${d.digital_signature?.slice(0, 12)}…`);
      qc.invalidateQueries({ queryKey: ["mm-surveyor-list"] });
      setPkg({ ...pkg, package_name: "" });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  return (
    <div className="space-y-6" data-testid="page-mining-surveyor-portal">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Registered Surveyor Portal</div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
          <Compass className="text-[#C99A2E]" /> Surveyor Submission Portal
        </h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1 max-w-4xl leading-relaxed">
          Authorised surveyors submit survey-grade coordinate packages for the mining cadastre migration. All submissions are digitally
          signed and enter the isolated staging environment. Surveyors cannot access the internal claim register.
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="cad-panel p-5">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Submit survey package</div>
          <div className="space-y-2 mt-3">
            <input value={pkg.surveyor_name} onChange={(e) => setPkg({ ...pkg, surveyor_name: e.target.value })}
                   placeholder="Surveyor full name" data-testid="msp-name"
                   className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
            <input value={pkg.surveyor_reg} onChange={(e) => setPkg({ ...pkg, surveyor_reg: e.target.value })}
                   placeholder="Registration (e.g. SG/2011/044)" data-testid="msp-reg"
                   className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
            <input value={pkg.package_name} onChange={(e) => setPkg({ ...pkg, package_name: e.target.value })}
                   placeholder="Survey package name" data-testid="msp-pkg"
                   className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
            <div className="grid grid-cols-2 gap-2">
              <select value={pkg.province} onChange={(e) => setPkg({ ...pkg, province: e.target.value })} data-testid="msp-province"
                      className="bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                {(ref.data?.provinces || []).map((p) => (<option key={p} value={p}>{p}</option>))}
              </select>
              <select value={pkg.commodity} onChange={(e) => setPkg({ ...pkg, commodity: e.target.value })} data-testid="msp-commodity"
                      className="bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                {(ref.data?.commodities || []).map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </div>
            <select value={pkg.declared_crs} onChange={(e) => setPkg({ ...pkg, declared_crs: e.target.value })} data-testid="msp-crs"
                    className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
              {(ref.data?.crs_catalogue || []).map((c) => (<option key={c.code} value={c.code}>{c.code} — {c.name}</option>))}
            </select>
            <button onClick={() => submit.mutate()} disabled={!pkg.surveyor_name || !pkg.package_name || submit.isPending}
                    data-testid="msp-submit"
                    className="cad-btn-primary w-full py-2 rounded-sm text-[13px] inline-flex items-center justify-center gap-1.5">
              {submit.isPending ? <Loader2 size={13} className="animate-spin" /> : <FileSignature size={13} />} Submit signed package
            </button>
          </div>
          <div className="mt-4 border-t cad-hairline pt-3">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.2em] text-[#7C93A5]">Surveyor capabilities</div>
            <ul className="mt-2 space-y-1 text-[12px] text-[#DCE3E8]">
              {["Submit survey-grade coordinates", "Upload digital survey packages", "Enter CRS details",
                "Apply digital signatures", "Track approval progress", "Correct rejected submissions"].map((c) => (
                <li key={c} className="flex items-center gap-1.5"><ShieldCheck size={11} className="text-[#6BB88A]" />{c}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="cad-panel p-5 lg:col-span-2">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Submission history</div>
          <table className="w-full mt-3 text-[12.5px]">
            <thead><tr className="text-left border-b cad-hairline">
              {["Surveyor", "Package", "Province", "Commodity", "CRS", "Signature", "Status"].map((h) => (
                <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
              ))}
            </tr></thead>
            <tbody className="cad-tbody">
              {(list.data || []).map((s) => (
                <tr key={s.id} data-testid={`msp-row-${s.id}`}>
                  <td className="px-2 py-1.5 text-[#F6F8FA]">{s.surveyor_name}<div className="text-[10px] font-mono-cad text-[#B3C2CC]">{s.surveyor_reg}</div></td>
                  <td className="px-2 py-1.5 text-[#DCE3E8]">{s.package_name}</td>
                  <td className="px-2 py-1.5 text-[#B3C2CC]">{s.province}</td>
                  <td className="px-2 py-1.5 text-[#B3C2CC]">{s.commodity}</td>
                  <td className="px-2 py-1.5 font-mono-cad text-[11px]">{s.declared_crs}</td>
                  <td className="px-2 py-1.5 font-mono-cad text-[10px] text-[#C99A2E]">{s.digital_signature?.slice(0, 10)}…</td>
                  <td className="px-2 py-1.5"><span className="cad-chip cad-chip-gold text-[9px] uppercase">{s.status?.replace(/_/g, " ")}</span></td>
                </tr>
              ))}
              {(!list.data || list.data.length === 0) &&
                <tr><td colSpan={7} className="px-2 py-4 text-center italic text-[#7C93A5] text-[12px]">No surveyor submissions yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
